/**
*
* ResourceOverviewPopup
*
*/

import React from 'react';
import PropTypes from 'prop-types';
import Checkbox from '@mui/material/Checkbox';
import { toast } from 'react-toastify';
import withResources from '../../../../../Admin/shared/ResourcesWrapper';
import SectionLoading from '@components/SectionLoading';
import Text from '@components/Text';
import ResourceOverview from '@shared/ResourceOverview';
import { TOAST_AUTO_CLOSE_TIME } from '@shared/constants';
import {
  Wrapper,
  SelectButton,
  ItemButton,
  HeaderWrapper,
  ResourcesList,
  OverviewSection,
  ResourceItem,
  OverviewWrapper,
  ResourceNameButton,
  SelectButtonWrapper,
  VirtualizedResourcesList
} from './styles';
import {connect} from "react-redux";
import {compose} from "redux";
import {
  clearResourcesData,
  getResources,
  resetOffset,
  setSearchText
} from "@containers/Admin/shared/ResourcesWrapper/actions";
import { getOverviewByTypeAndID } from '@containers/Admin/shared/ResourcesWrapper/service/overviewService';
import { withTranslation } from 'react-i18next';
import {Search} from './Search'
const SOURCE = 'popup';
import {debounce} from "lodash";

const getResourceId = (resource) => resource && (resource.id || resource._id);
const getUniqueResources = (resources) => Array.from(resources.reduce((uniqueResources, resource) => {
  const resourceId = getResourceId(resource);
  if (resourceId && !uniqueResources.has(resourceId)) uniqueResources.set(resourceId, resource);
  return uniqueResources;
}, new Map()).values());

class ResourceSelectionPopup extends React.PureComponent {
  state = {
    selectedItems: [],
  };

  componentDidMount() {
    const { dispatchLoadFilteredResources, resourceType, searchText } = this.props;
    dispatchLoadFilteredResources(resourceType, searchText);
  }

  componentDidUpdate(prevProps) {
    const {searchText, dispatchClearResourcesData} = this.props
    if(prevProps.searchText.length > searchText.length) {
      dispatchClearResourcesData()
    }
    if(prevProps.searchText !== searchText) {
      this.searchWithDebounce()
    }
  }

  componentWillUnmount() {
    const {dispatchClearResourcesData} = this.props
    dispatchClearResourcesData()
  }

  onResourceSelected = ({ id, _id }) => () => {
    const { dispatchLoadItem, resourceType } = this.props;
    dispatchLoadItem(resourceType, id || _id, SOURCE);
  }

  onSelect = () => {
    const { popup: { resource } } = this.props;
    this.props.onItemSelected(resource);
    this.props.onClose();
  }

  showSelectionError = () => {
    toast.error(this.props.t('Informative.Negative.requestFailed'), {
      autoClose: TOAST_AUTO_CLOSE_TIME,
      position: 'bottom-right',
    });
  }

  onItemsSelected = async () => {
    if (!this.props.onItemsSelected) return;
    if (this.state.selectedItems.length === 0) {
      this.props.onClose();
      return;
    }
    try {
      const results = await Promise.all(this.state.selectedItems.map(
        (resource) => getOverviewByTypeAndID(this.props.resourceType, getResourceId(resource)),
      ));
      if (results.some((result) => !result || !result.overview)) {
        this.showSelectionError();
        return;
      }
      this.props.onItemsSelected(results.map((result) => result.overview));
      this.props.onClose();
    } catch (error) {
      this.showSelectionError();
    }
  }

  onItemChecked = (resource, checked) => {
    const resourceId = getResourceId(resource);
    this.onResourceSelected(resource)();
    this.setState(({ selectedItems }) => ({
      selectedItems: checked
        ? getUniqueResources([...selectedItems, resource])
        : selectedItems.filter((item) => getResourceId(item) !== resourceId),
    }));
  }

  searchWithDebounce = debounce( async () => {
    this.props.dispatchResetOffset()
    this.props.dispatchLoadFilteredResources(this.props.resourceType, this.props.searchText)
  }, 300)

  handleInput = (e) => {
    this.props.dispatchSetSearchText(e.target.value)
  }

  renderNoResourceSelected = () => (<Text className="noOverview">
    {this.props.t('Informative.Negative.noResource')}
  </Text>)

  renderResource = (resource) => {
    const { multiple, popup } = this.props;
    const resourceId = getResourceId(resource);
    const isPreviewed = multiple && resourceId === getResourceId(popup.resource);
    const checked = multiple && this.state.selectedItems.some(
      (item) => getResourceId(item) === resourceId,
    );
    return resource?.isTemplate&&(
    <ResourceItem
      className="resourceItem"
      key={resourceId}
      multiple={multiple}
      isPreviewed={isPreviewed}
    >
      {multiple ? (
        <React.Fragment>
          <ResourceNameButton
            type="button"
            aria-current={isPreviewed ? 'true' : undefined}
            onClick={this.onResourceSelected(resource)}
          >
            {resource.name || resource.title}
          </ResourceNameButton>
          <Checkbox
            checked={checked}
            onChange={(event) => this.onItemChecked(resource, event.target.checked)}
            inputProps={{ 'aria-label': resource.name || resource.title }}
          />
        </React.Fragment>
      ) : (
        <React.Fragment>
          <Text className="name">{resource.name || resource.title}</Text>
          <ItemButton className="selectResourceItem" onClick={this.onResourceSelected(resource)} >
            {this.props.t('Actions.Instance.select')}
          </ItemButton>
        </React.Fragment>
      )}
    </ResourceItem>)}

  renderLoadingPopup = () => (<Wrapper>
    <div className="loadingResources">
      <Text>
        {this.props.t('Informative.Statuses.loadingResources')}
      </Text>
      <SectionLoading />
    </div>
  </Wrapper>)

  renderOverviewSection = (loading, resource) => {
    const { multiple, onClose, onEdit, onExpand, resourceType } = this.props;
    return (
      <React.Fragment>
        <OverviewWrapper>
          <ResourceOverview
            className="overview"
            readOnly
            disableEdit
            loading={loading}
            onEdit={(...args) => {
              onClose();
              onEdit(...args);
            }}
            onExpand={onExpand}
            type={resourceType}
            resource={resource}
          />
        </OverviewWrapper>
        <SelectButtonWrapper>
          <SelectButton
            className={multiple ? 'selectResources' : 'selectResource'}
            onClick={multiple ? this.onItemsSelected : this.onSelect}
          >
            {this.props.t('Actions.Instance.select')}
            {multiple ? ` (${this.state.selectedItems.length})` : ''}
          </SelectButton>
        </SelectButtonWrapper>
      </React.Fragment>
    );
  }

  render() {
    const { items, searchText, popup, resourceType, dispatchLoadFilteredResources, multiple } = this.props;
    if (!popup) return this.renderLoadingPopup();
    const { loading, resource } = popup;
    return (
      <Wrapper>
        <HeaderWrapper>
          <Text size="H1" className="headerText">
            {this.props.t('Actions.Multiple.selectResource', {resourceType: resourceType})}
          </Text>
          <Search valueText={searchText} setValueText={this.handleInput} />
        </HeaderWrapper>
        <ResourcesList className="resourcesList">
          <VirtualizedResourcesList
              data={items}
              endReached={(lastItemIndex) => {
                const {type, isLastPage, offset} = this.props.resource
                !isLastPage && lastItemIndex + 1 === offset && dispatchLoadFilteredResources(type, this.props.searchText, 'scroll')
              }}
              itemContent={(_, item) => this.renderResource(item)}
          />
        </ResourcesList>
        <OverviewSection className="overviewSection">
          {
            ((!loading && !resource) || (resource && resource.type !== resourceType))
              ? multiple
                ? <React.Fragment>
                  <OverviewWrapper>{this.renderNoResourceSelected()}</OverviewWrapper>
                  <SelectButtonWrapper>
                    <SelectButton className="selectResources" onClick={this.onItemsSelected}>
                      {this.props.t('Actions.Instance.select')} ({this.state.selectedItems.length})
                    </SelectButton>
                  </SelectButtonWrapper>
                </React.Fragment>
                : this.renderNoResourceSelected()
              : this.renderOverviewSection(loading, resource)
          }
        </OverviewSection>
      </Wrapper>
    );
  }
}

ResourceSelectionPopup.propTypes = {
  resourceType: PropTypes.string, // gets from popup object
  onItemSelected: PropTypes.func, // gets from popup object
  onItemsSelected: PropTypes.func,
  multiple: PropTypes.bool,
  searchText: PropTypes.string,
  items: PropTypes.array,

  popup: PropTypes.shape({
    loading: PropTypes.bool,
    resource: PropTypes.object,
  }),
  loadingResources: PropTypes.bool,
  resources: PropTypes.array,
  onEdit: PropTypes.func,
  onClose: PropTypes.func,
  onExpand: PropTypes.func,
  dispatchClearResourcesData: PropTypes.func,
  dispatchLoadItem: PropTypes.func,
  dispatchLoadFilteredResources: PropTypes.func,
  dispatchSetSearchText: PropTypes.func,
  dispatchResetOffset: PropTypes.func
};

ResourceSelectionPopup.defaultProps = {
  multiple: false,
};

const mapStateToProps = state => {
  const searchText = state.toJS()?.resourceManager.searchText //.getIn(['resourceManager', 'searchText'])
  const items = state.toJS()?.resourceManager.resources //.getIn(['resourceManager', 'resources'])

  return {
    searchText,
    items,
    resource: state.toJS()?.resourceManager
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    dispatchClearResourcesData: () => dispatch(clearResourcesData()),
    dispatchLoadFilteredResources: (type, search, loadType) => dispatch(getResources(type, search, loadType)),
    dispatchSetSearchText: (text) => dispatch(setSearchText(text)),
    dispatchResetOffset: () => dispatch(resetOffset())
  }
}

const withConnect = connect(mapStateToProps, mapDispatchToProps)

export default compose(
    withResources,
    withConnect,
    withTranslation()
)(ResourceSelectionPopup)